import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// copyToClipboard tries the async Clipboard API first, then falls back to a
// hidden textarea + document.execCommand("copy") for non-secure contexts
// (HTTP origins other than localhost, where navigator.clipboard is undefined).
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// --- Exact-precision helpers (no rounding, trim trailing zeros only) ---

// numberToPlainDecimal: render a finite number as a plain decimal string,
// avoiding JS exponent notation (e.g. 1e-7 → "0.0000001"). Falls back to
// exponent form for |value| >= 1e21 (known limitation).
function numberToPlainDecimal(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "0";
  const s = String(value);
  if (!/[eE]/.test(s)) return s;
  if (Math.abs(value) >= 1e21) return s;
  return value.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 20 });
}

// trimDecimalZeros: normalize a numeric value to its exact decimal representation
// with trailing zeros stripped. No rounding, no locale grouping separators.
// Number() is used ONLY for the zero/finite guard (never for display), which is
// safe for backend NUMERIC(20,9) strings; the displayed string is never re-rounded.
function trimDecimalZeros(value: string | number): string {
  const raw = typeof value === "number" ? numberToPlainDecimal(value) : value.trim();
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return "0";
  const sign = raw.startsWith("-") ? "-" : "";
  // strip sign and any leading zeros; restore "0." for values like ".5"/"00.5".
  const abs = raw.replace(/^[-+]?0*/, "").replace(/^\./, "0.");
  if (abs === "") return "0";
  const trimmed = abs.includes(".") ? abs.replace(/0+$/, "").replace(/\.$/, "") : abs;
  return `${sign}${trimmed}`;
}

// absDecimalInput: strip leading sign from a numeric value, returning the
// magnitude portion. Used by credit/signed formatters to avoid double-signing.
function absDecimalInput(value: string | number): string | number {
  if (typeof value === "number") return Math.abs(value);
  return value.trim().replace(/^[-+]/, "");
}

// formatSTAmount: backend ST decimal string → exact plain decimal,
// trailing zeros trimmed. Never rounds, never uses locale grouping.
export function formatSTAmount(value: string | number): string {
  return trimDecimalZeros(value);
}

// formatUSD: exact precision with sign before "$" (e.g. "-$1.23", "$123.456789").
export function formatUSD(value: string | number): string {
  const trimmed = trimDecimalZeros(value);
  if (trimmed === "0") return "$0";
  if (trimmed.startsWith("-")) {
    return `-$${trimmed.slice(1)}`;
  }
  return `$${trimmed}`;
}

// formatUSDExact: alias for formatUSD — both render exact stored precision.
export function formatUSDExact(value: string | number): string {
  return formatUSD(value);
}

// clampDecimalPlaces trims a numeric input string to at most `places` decimal
// digits as the user types (truncating extra digits), while leaving a partial
// entry like "1." intact. Caps on-screen precision for money inputs (9 dp for
// ST amounts) and discount rates (2 dp); the backend still rounds on submit.
export function clampDecimalPlaces(value: string, places: number): string {
  const m = /^(-?\d*)(?:\.(\d*))?$/.exec(value);
  if (!m) return value;
  const frac = m[2];
  if (frac !== undefined && frac.length > places) {
    return `${m[1]}.${frac.slice(0, places)}`;
  }
  return value;
}

// formatCashUSD: available cash portion of st_balance — non-negative values
// only. Negative st_balance (credit in use) renders as "$0".
export function formatCashUSD(st: string | number): string {
  const n = typeof st === "string" ? Number(st) : st;
  if (!Number.isFinite(n) || n <= 0) return formatUSD(0);
  return formatUSD(st);
}

// formatCreditUsedUSD: magnitude of the negative part of st_balance.
// Non-negative st_balance (no credit used) renders as "$0".
export function formatCreditUsedUSD(st: string | number): string {
  const n = typeof st === "string" ? Number(st) : st;
  if (!Number.isFinite(n) || n >= 0) return formatUSD(0);
  return formatUSD(absDecimalInput(st));
}

// formatSignedUSD: delta with explicit leading sign — "+$12.34" / "-$5.00" / "$0".
export function formatSignedUSD(value: string | number): string {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n) || n === 0) return formatUSD(0);
  const sign = n > 0 ? "+" : "-";
  return sign + formatUSD(absDecimalInput(value));
}

// formatDiscount renders a TeamBillingConfig.discount_rate decimal string
// (e.g. "0.8") as a human-friendly discount, e.g. zh "8 折 / 80%" / en "80%".
// A rate >= 1 (no discount) — or malformed/non-finite input — renders via
// the "billing.noDiscount" translation key instead of a bogus percentage.
export function formatDiscount(
  rate: string | number,
  language: "zh" | "en",
  t: (key: string) => string,
): string {
  const n = typeof rate === "string" ? Number(rate) : rate;
  if (!isFinite(n) || n >= 1) return t("billing.noDiscount");
  const percent = Math.round(n * 100);
  if (language !== "zh") return `${percent}%`;
  const zhe = Math.round(n * 100) / 10; // e.g. 0.85 -> 8.5
  const zheStr = Number.isInteger(zhe) ? String(zhe) : zhe.toFixed(1);
  return `${zheStr}${t("billing.discountUnit")} / ${percent}%`;
}

// formatBillingCycle renders a TeamBillingConfig.billing_cycle enum via
// translations.ts copy. Unknown values fall back to monthly to match the
// backend default (see BillingConfigService.Get).
export function formatBillingCycle(
  cycle: string,
  t: (key: string) => string,
): string {
  return cycle === "weekly" ? t("billing.cycleWeekly") : t("billing.cycleMonthly");
}

export function exportToCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((row) => row.map(escape).join(",")),
  ];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
